import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * EditableCell — click-to-edit inline cell used across SEO Performance,
 * Payroll and any other spreadsheet-style tables.
 *
 *  value      current value shown (number or string)
 *  onSave     async (parsed) => void — called on blur/Enter when the value
 *             actually changed. Throwing is fine; caller shows the toast.
 *  type       "number" (default) | "text"
 *  prefix/suffix   decorators around the display value (e.g. "₹", " kg")
 *  readOnly   disables click-to-edit; renders as plain text
 *  overridden shows a subtle fuchsia badge + ✎ superscript (Payroll uses
 *             this to mark manual overrides of computed values)
 *  align      "right" (default) | "left"
 *  step/min/max   forwarded to <input> for number type
 *  formatDisplay  optional (value) => string override for the read view
 *  testId     stable data-testid; input gets `${testId}-input`
 */
export default function EditableCell({
  value, onSave, type = "number",
  prefix = "", suffix = "", readOnly = false, overridden = false,
  align = "right", step, min, max, formatDisplay, testId,
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);

  const commit = async () => {
    setEditing(false);
    if (String(val ?? "") === String(value ?? "")) return;
    const parsed = type === "number" ? (val === "" || val === null ? null : Number(val)) : val;
    if (type === "number" && parsed !== null && Number.isNaN(parsed)) { setVal(value); return; }
    await onSave(parsed);
  };

  if (editing && !readOnly) {
    return (
      <Input
        autoFocus
        type={type}
        {...(step !== undefined ? { step } : {})}
        {...(min !== undefined ? { min } : {})}
        {...(max !== undefined ? { max } : {})}
        value={val ?? ""}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { setVal(value); setEditing(false); }
        }}
        className="h-7 text-xs px-2 min-w-[70px]"
        data-testid={`${testId}-input`}
      />
    );
  }

  const isEmpty = value === null || value === undefined || value === "";
  let display;
  if (isEmpty) display = "—";
  else if (formatDisplay) display = formatDisplay(value);
  else display = `${prefix}${value}${suffix}`;

  return (
    <span
      onClick={() => !readOnly && setEditing(true)}
      className={`inline-block ${align === "right" ? "text-right" : "text-left"} min-w-[60px] px-1.5 py-0.5 rounded ${
        readOnly ? "text-slate-600" : "cursor-pointer hover:bg-purple-50 hover:ring-1 hover:ring-purple-200"
      } ${overridden ? "text-fuchsia-700 font-semibold" : ""} tabular-nums`}
      title={overridden ? "Manually overridden" : (readOnly ? undefined : "Click to edit")}
      data-testid={testId}
    >
      {display}{overridden && <sup className="text-[8px] ml-0.5 opacity-70">✎</sup>}
    </span>
  );
}
